import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUsers, AdminUser } from '@/hooks/useAdminUsers';
import { PRICING_PLANS } from '@/constants/pricing';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Ban, Edit, Eye, RefreshCw, Users, RotateCcw, Crown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';

export const UserManagement = () => {
  const navigate = useNavigate();
  const { 
    users, 
    loading, 
    updateUserRole, 
    updateUserPlan,
    updateUserLimits,
    resetUserUsage,
    suspendUser, 
    fetchUsers 
  } = useAdminUsers();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editValues, setEditValues] = useState({
    plan: '',
    role: '',
    credits: 0,
    imagesLimit: 0,
    scriptsLimit: 0,
    audioMinutesLimit: 0,
    dailyLimit: 0
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'all' || user.plan === planFilter;
    return matchesSearch && matchesPlan;
  });

  const openEditDialog = (user: AdminUser) => {
    setEditingUser(user);
    setEditValues({
      plan: user.plan || 'free',
      role: user.role || 'user',
      credits: user.total_credits || 0,
      imagesLimit: user.images_limit || 5,
      scriptsLimit: user.scripts_limit || 5,
      audioMinutesLimit: user.audio_minutes_limit || 5,
      dailyLimit: user.daily_generation_limit || 10
    });
  };

  const handleSaveChanges = async () => {
    if (!editingUser) return;
    
    // Update role if changed
    if (editValues.role !== editingUser.role) {
      await updateUserRole(editingUser.id, editValues.role);
    }
    
    // Update plan if changed (this will also update limits according to the plan)
    if (editValues.plan !== editingUser.plan) {
      await updateUserPlan(editingUser.id, editValues.plan);
    } else {
      // If plan didn't change, update individual limits
      await updateUserLimits(editingUser.id, {
        imagesLimit: editValues.imagesLimit,
        scriptsLimit: editValues.scriptsLimit,
        audioMinutesLimit: editValues.audioMinutesLimit,
        dailyLimit: editValues.dailyLimit,
        credits: editValues.credits
      });
    }
    
    setEditingUser(null);
  };

  const getPlanBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'team': return 'default';
      case 'pro': return 'default';
      case 'starter': return 'secondary';
      default: return 'outline';
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>User Management</CardTitle>
            <Badge variant="secondary">{users.length} users</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchUsers()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="team">Team</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Limits</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No users found matching your search' : 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} className={user.is_banned ? 'bg-destructive/5' : ''}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.full_name || 'N/A'}</span>
                        <span className="text-xs text-muted-foreground">{user.email || 'No email'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getPlanBadgeVariant(user.plan)} className="capitalize">
                        {user.plan === 'team' && <Crown className="h-3 w-3 mr-1" />}
                        {user.plan}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={user.role === 'admin' || user.role === 'superadmin' ? 'default' : 'secondary'}
                        className="capitalize"
                      >
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Images:</span>
                          <span>{user.monthly_images_used}/{user.images_limit}</span>
                        </div>
                        <Progress value={getUsagePercentage(user.monthly_images_used, user.images_limit)} className="h-1" />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>Img: {user.images_limit}</div>
                      <div>Scr: {user.scripts_limit}</div>
                      <div>Aud: {user.audio_minutes_limit}m</div>
                    </TableCell>
                    <TableCell>
                      {user.is_banned ? (
                        <Badge variant="destructive">Banned</Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          title="Edit User"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resetUserUsage(user.id)}
                          title="Reset Usage"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => suspendUser(user.id)}
                          title={user.is_banned ? 'Unban User' : 'Ban User'}
                        >
                          <Ban className={`h-4 w-4 ${user.is_banned ? 'text-green-600' : 'text-destructive'}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User: {editingUser?.email}</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="plan" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="plan">Plan & Role</TabsTrigger>
              <TabsTrigger value="limits">Limits</TabsTrigger>
              <TabsTrigger value="credits">Credits</TabsTrigger>
            </TabsList>
            
            <TabsContent value="plan" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Subscription Plan</Label>
                  <Select
                    value={editValues.plan}
                    onValueChange={(value) => setEditValues(prev => ({ ...prev, plan: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRICING_PLANS).map(([key, plan]) => (
                        <SelectItem key={key} value={key}>
                          {plan.name} - {plan.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Changing plan will update all limits automatically
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>User Role</Label>
                  <Select
                    value={editValues.role}
                    onValueChange={(value) => setEditValues(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="moderator">Moderator</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="superadmin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Show plan features preview */}
              {editValues.plan && PRICING_PLANS[editValues.plan as keyof typeof PRICING_PLANS] && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">Plan Features:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {PRICING_PLANS[editValues.plan as keyof typeof PRICING_PLANS].features.slice(0, 4).map((feature, i) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="limits" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Images Limit (monthly)</Label>
                  <Input
                    type="number"
                    value={editValues.imagesLimit}
                    onChange={(e) => setEditValues(prev => ({ ...prev, imagesLimit: Number(e.target.value) }))}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scripts Limit (monthly)</Label>
                  <Input
                    type="number"
                    value={editValues.scriptsLimit}
                    onChange={(e) => setEditValues(prev => ({ ...prev, scriptsLimit: Number(e.target.value) }))}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Audio Minutes Limit (monthly)</Label>
                  <Input
                    type="number"
                    value={editValues.audioMinutesLimit}
                    onChange={(e) => setEditValues(prev => ({ ...prev, audioMinutesLimit: Number(e.target.value) }))}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Daily Generation Limit</Label>
                  <Input
                    type="number"
                    value={editValues.dailyLimit}
                    onChange={(e) => setEditValues(prev => ({ ...prev, dailyLimit: Number(e.target.value) }))}
                    min="0"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Note: These limits will be overwritten if you change the plan
              </p>
            </TabsContent>
            
            <TabsContent value="credits" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Total Credits</Label>
                <Input
                  type="number"
                  value={editValues.credits}
                  onChange={(e) => setEditValues(prev => ({ ...prev, credits: Number(e.target.value) }))}
                  min="0"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditValues(prev => ({ ...prev, credits: prev.credits + 10 }))}
                >
                  +10
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditValues(prev => ({ ...prev, credits: prev.credits + 50 }))}
                >
                  +50
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditValues(prev => ({ ...prev, credits: prev.credits + 100 }))}
                >
                  +100
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveChanges}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
